import React, { useEffect, useState } from 'react';
import { Play, X, ExternalLink } from 'lucide-react';
import { VIDEOS, SOCIAL_LINKS } from '../mock/mock';

export default function Videos() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') setActive(null); };
    document.addEventListener('keydown', onEsc);
    return () => document.removeEventListener('keydown', onEsc);
  }, []);

  useEffect(() => {
    if (active) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [active]);

  return (
    <section id="videos" className="text-cream py-20 md:py-28 relative overflow-hidden" style={{backgroundColor: '#7B96B5'}}>
      <div className="max-w-[1600px] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10 md:mb-14">
          <div>
            <p className="font-anton uppercase tracking-[0.3em] text-pink-hot text-xs mb-4">— Videos</p>
            <h2 className="mega text-cream text-6xl md:text-8xl">Watch</h2>
          </div>
          <a href={SOCIAL_LINKS.youtube} target="_blank" rel="noreferrer"
             className="bg-pink-hot text-cream hover:bg-[#D64A8A] px-6 py-3 font-anton uppercase tracking-widest text-sm inline-flex items-center gap-2 transition-colors rounded-full">
            YouTube Channel <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Uniform 3-col grid — all videos same size */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
          {VIDEOS.map((v) => (
            <button
              key={v.id}
              onClick={() => setActive(v.id)}
              className="group text-left relative"
            >
              <div className="relative aspect-video overflow-hidden bg-brown">
                <img
                  src={v.thumb}
                  alt={v.title}
                  onError={(e) => { e.currentTarget.src = `https://img.youtube.com/vi/${v.id}/hqdefault.jpg`; }}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-brown-deep/20 group-hover:bg-brown-deep/0 transition-colors duration-300" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-cream/90 group-hover:bg-pink-hot flex items-center justify-center transition-all duration-300 group-hover:scale-110">
                    <Play className="w-6 h-6 md:w-7 md:h-7 text-brown group-hover:text-cream fill-current ml-0.5 transition-colors" />
                  </div>
                </div>
                <div className="absolute top-3 right-3 font-anton uppercase tracking-widest text-[10px] bg-brown-deep/80 text-cream px-2 py-0.5">
                  {v.duration}
                </div>
              </div>
              <div className="mt-3">
                <p className="font-anton uppercase tracking-widest text-[10px] text-pink">{v.type}</p>
                <h4 className="font-body text-cream text-sm md:text-[15px] leading-snug line-clamp-2 mt-1">{v.title}</h4>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Player Modal */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-brown-deep/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
          onClick={() => setActive(null)}
        >
          <button
            onClick={() => setActive(null)}
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-cream text-brown hover:bg-pink-hot hover:text-cream flex items-center justify-center transition-colors"
            aria-label="close"
          >
            <X className="w-5 h-5" />
          </button>
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-5xl aspect-video bg-brown-deep overflow-hidden relative"
          >
            <iframe
              className="w-full h-full"
              src={`https://www.youtube.com/embed/${active}?autoplay=1&rel=0`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
}
